import { useEffect } from 'react';

import { StackActions } from '@react-navigation/native';
import { useNavigation } from 'expo-router';

/**
 * Resets the calling tab layout's nested stack to its first screen when the user switches to
 * another tab. Works with both classic `Tabs` and `NativeTabs` (which has no `popToTopOnBlur`).
 *
 * A tab also receives `blur` when a root-level screen (e.g. a `(modals)` route) covers the
 * tabs; in that case the tab is still the selected one, so the stack is kept and dismissing
 * the modal returns to the same nested screen.
 */
export const usePopToTopOnTabSwitch = () => {
  const navigation = useNavigation();

  useEffect(() => {
    const unsubscribe = navigation.addListener('blur', event => {
      const tabsState = navigation.getState();

      if (!tabsState) {
        return;
      }

      const blurredTabKey = event.target;
      const selectedTabKey = tabsState.routes[tabsState.index]?.key;
      const isStillSelectedTab = selectedTabKey === blurredTabKey;

      if (isStillSelectedTab) {
        return;
      }

      const blurredTabStack = tabsState.routes.find(route => route.key === blurredTabKey)?.state;

      const isStack = blurredTabStack?.type === 'stack' && Boolean(blurredTabStack.key);
      const hasScreensAboveFirst = (blurredTabStack?.index ?? 0) > 0;

      if (!isStack || !hasScreensAboveFirst) {
        return;
      }

      navigation.dispatch({ ...StackActions.popToTop(), target: blurredTabStack.key });
    });

    return unsubscribe;
  }, [navigation]);
};
